// Price ticks for the chart: the last hour of per-block mids from our API, then live updates. On mainnet
// the live source is Hyperliquid's `bbo` WebSocket (best bid/ask, pushed on each HyperCore block where it
// changes), whose (bid+ask)/2 is the mid the oracle settles at; on a local chain it is the mid pushed by
// our own feed every block.
import { useEffect, useState } from 'react';

import { config, MARKETS } from '../config.ts';
import { reportError } from './errors.ts';
import { hlEnabled } from './hl.ts';

export type Tick = { t: number; px: number };

const HISTORY_SEC = 3_600;
const MAX_TICKS = 50_000;
const HL_WS = 'wss://api.hyperliquid.xyz/ws';
/** HYPE's best bid/ask moves many times a second: this much silence is a socket the browser lost */
const BBO_STALE_MS = 30_000;

const buffers = new Map<number, Tick[]>();
/** Bumped on every change: buffers are mutated in place, and stop growing once full */
const versions = new Map<number, number>();
const listeners = new Map<number, Set<() => void>>();
const loaded = new Set<number>();

function emit(market: number) {
  versions.set(market, (versions.get(market) ?? 0) + 1);
  for (const l of listeners.get(market) ?? []) l();
}

/** Appends a tick if it moves the series forward in time; same-millisecond updates replace the last one */
export function pushTick(market: number, tick: Tick): void {
  const buf = buffers.get(market) ?? [];
  const last = buf[buf.length - 1];
  if (last && tick.t < last.t) return;
  if (last && tick.t === last.t) buf[buf.length - 1] = tick;
  else buf.push(tick);
  if (buf.length > MAX_TICKS) buf.splice(0, buf.length - MAX_TICKS);
  buffers.set(market, buf);
  emit(market);
}

async function loadHistory(market: number, priceScale: number): Promise<void> {
  if (loaded.has(market)) return;
  loaded.add(market);
  try {
    const res = await fetch(`${config.relayerUrl}/history/${market}?sec=${HISTORY_SEC}`);
    const rows = (await res.json()) as [number, number][];
    const history = rows.map(([t, m]) => ({ t, px: m / priceScale }));
    // live ticks may have arrived meanwhile: keep them after the history
    const live = buffers.get(market) ?? [];
    const cut = history.length ? history[history.length - 1]!.t : 0;
    buffers.set(market, [...history, ...live.filter((x) => x.t > cut)]);
    emit(market);
  } catch {
    loaded.delete(market); // retried on the next mount
  }
}

/** Hyperliquid bbo stream for one coin, reconnecting; returns a stop function */
function streamBbo(market: number): () => void {
  const coin = MARKETS[market]?.hlCoin;
  if (!coin) return () => {};
  let ws: WebSocket | undefined;
  let closed = false;
  let retry = 0;
  let lastAt = Date.now();
  let logged = false;
  const open = () => {
    ws = new WebSocket(HL_WS);
    lastAt = Date.now();
    ws.onopen = () => {
      retry = 0;
      ws!.send(JSON.stringify({ method: 'subscribe', subscription: { type: 'bbo', coin } }));
    };
    ws.onmessage = (e) => {
      lastAt = Date.now();
      try {
        const msg = JSON.parse(String(e.data)) as { data?: { coin?: string; time?: number; bbo?: ({ px: string } | null)[] } };
        const d = msg.data;
        if (!d || d.coin !== coin || !d.bbo?.[0] || !d.bbo[1]) return;
        pushTick(market, { t: d.time ?? Date.now(), px: (Number(d.bbo[0].px) + Number(d.bbo[1].px)) / 2 });
      } catch (err) {
        if (!logged) reportError('bbo frame', err);
        logged = true;
      }
    };
    ws.onclose = () => {
      if (!closed) setTimeout(open, Math.min(15_000, 500 * 2 ** retry++));
    };
  };
  open();
  // a socket lost without a close event (sleep, network change) would leave the chart frozen: replace it
  const watchdog = setInterval(() => {
    if (!closed && ws?.readyState === WebSocket.OPEN && Date.now() - lastAt > BBO_STALE_MS) ws.close();
  }, 5_000);
  return () => {
    closed = true;
    clearInterval(watchdog);
    ws?.close();
  };
}

/** Ticks of `market` and a version that changes with each new one */
export function useTicks(market: number, priceScale: number | undefined): { ticks: Tick[]; version: number } {
  const [, bump] = useState(0);
  useEffect(() => {
    const l = () => bump((x) => x + 1);
    const set = listeners.get(market) ?? new Set();
    set.add(l);
    listeners.set(market, set);
    if (priceScale) void loadHistory(market, priceScale);
    const stop = hlEnabled ? streamBbo(market) : () => {};
    return () => {
      set.delete(l);
      stop();
    };
  }, [market, priceScale]);
  return { ticks: buffers.get(market) ?? [], version: versions.get(market) ?? 0 };
}
